import React from "react";
import { Plus } from "lucide-react";
import { FormField } from "../ui/form-field";

const Field = FormField;

// ─────────────────────────────────────────────────────────────────────────
// Opções fixas da aba "Cadastrar casa"
// ─────────────────────────────────────────────────────────────────────────
const PROPERTY_SUBTYPES = ["Casa única", "Sobrado", "Apartamento", "Outro"];
const ROOMS_OPTIONS = [
  "Cozinha", "Sala de família", "Sala de estar", "Sala de jantar",
  "Escadas", "Sótão", "Porão", "Garagem",
  "Lavanderia", "Escritório", "Sala de recreação", "Corredor", "Outro",
];
const CLEANING_TYPES = [
  "Padrão", "Profunda", "Festa / Ocasião especial",
  "Janelas", "Mudança (entrada/saída)", "Outro",
];
const ADDITIONAL_SERVICES = [
  "Limpeza de forno", "Geladeira", "Lavagem de paredes",
  "Persianas", "Luminárias", "Encerar piso",
];

function CheckChip({ checked, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors text-left ${
        checked
          ? "bg-blue-50 border-blue-400 text-blue-900"
          : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
      }`}
    >
      <span
        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
          checked ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300"
        }`}
      >
        {checked ? "✓" : ""}
      </span>
      <span className="flex-1">{children}</span>
    </button>
  );
}

function ChipGroup({ label, options, value, onChange, testIdPrefix, cols = 4 }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (arr.includes(opt)) onChange(arr.filter((x) => x !== opt));
    else onChange([...arr, opt]);
  };
  const colsCls = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4", 5: "sm:grid-cols-5", 7: "sm:grid-cols-7" }[cols] || "sm:grid-cols-4";
  return (
    <div>
      {label && (
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
          {label}
        </div>
      )}
      <div className={`grid grid-cols-2 ${colsCls} gap-2`}>
        {options.map((opt) => {
          const key = typeof opt === "string" ? opt : opt.v;
          const lbl = typeof opt === "string" ? opt : opt.l;
          return (
            <CheckChip
              key={key}
              checked={arr.includes(key)}
              onClick={() => toggle(key)}
              testId={testIdPrefix ? `${testIdPrefix}-${key}` : undefined}
            >
              {lbl}
            </CheckChip>
          );
        })}
      </div>
    </div>
  );
}

function YesNoToggle({ label, value, onChange, testIdPrefix }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
        {label}
      </div>
      <div className="flex gap-2">
        <CheckChip
          checked={value === true}
          onClick={() => onChange(value === true ? null : true)}
          testId={testIdPrefix ? `${testIdPrefix}-yes` : undefined}
        >
          Sim
        </CheckChip>
        <CheckChip
          checked={value === false}
          onClick={() => onChange(value === false ? null : false)}
          testId={testIdPrefix ? `${testIdPrefix}-no` : undefined}
        >
          Não
        </CheckChip>
      </div>
    </div>
  );
}

function DollarInput({ testId, value, onChange }) {
  return (
    <div className="relative">
      <span
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none font-semibold"
        aria-hidden="true"
      >
        $
      </span>
      <input
        type="number"
        step="0.01"
        min="0"
        data-testid={testId}
        className="cm-input"
        style={{ paddingLeft: "28px" }}
        placeholder="0.00"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function HouseDetailsForm({ form, setForm }) {
  const f = form;
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="client-house-form">
      {/* PETS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <YesNoToggle
          label="Pets"
          value={f.has_pets}
          onChange={(v) => upd("has_pets", v)}
          testIdPrefix="house-pets"
        />
        <Field label="Nome do(s) Pet(s)">
          <input
            data-testid="house-pet-names"
            className={`cm-input ${f.has_pets !== true ? "opacity-50" : ""}`}
            placeholder="Rex, Mia…"
            value={f.pet_names || ""}
            onChange={(e) => upd("pet_names", e.target.value)}
            disabled={f.has_pets !== true}
          />
        </Field>
      </div>

      {/* TIPO DE IMÓVEL */}
      <ChipGroup
        label="Tipo de Imóvel"
        options={PROPERTY_SUBTYPES}
        value={f.property_subtype ? [f.property_subtype] : []}
        onChange={(arr) => upd("property_subtype", arr[arr.length - 1] || "")}
        testIdPrefix="house-subtype"
        cols={4}
      />

      {/* Dimensões */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Nº de Quartos">
          <input type="number" min="0" data-testid="house-bedrooms" className="cm-input"
            value={f.bedrooms ?? ""} onChange={(e) => upd("bedrooms", e.target.value)} />
        </Field>
        <Field label="Nº de Banheiros">
          <input type="number" min="0" data-testid="house-bathrooms" className="cm-input"
            value={f.bathrooms ?? ""} onChange={(e) => upd("bathrooms", e.target.value)} />
        </Field>
        <Field label="Metragem (sqft)">
          <input type="number" min="0" data-testid="house-sqft" className="cm-input"
            value={f.square_feet ?? ""} onChange={(e) => upd("square_feet", e.target.value)} />
        </Field>
      </div>

      <CollapsibleChipGroup
        label="Cômodos a serem limpos"
        emptyHint="Cozinha, salas, escadas, garagem, lavanderia…"
        options={ROOMS_OPTIONS}
        value={f.rooms_to_clean}
        onChange={(v) => upd("rooms_to_clean", v)}
        testIdPrefix="house-rooms"
        cols={4}
      />

      <CollapsibleChipGroup
        label="Tipo de Limpeza"
        emptyHint="Padrão, profunda, janelas, mudança…"
        options={CLEANING_TYPES}
        value={f.cleaning_types}
        onChange={(v) => upd("cleaning_types", v)}
        testIdPrefix="house-cleaning-type"
        cols={3}
      />

      <YesNoToggle label="Você Fornecerá os Produtos de Limpeza?"
        value={f.supplies_provided} onChange={(v) => upd("supplies_provided", v)}
        testIdPrefix="house-supplies" />

      <CollapsibleChipGroup
        label="Serviços Adicionais"
        emptyHint="Limpeza de forno, geladeira, persianas…"
        options={ADDITIONAL_SERVICES}
        value={f.additional_services}
        onChange={(v) => upd("additional_services", v)}
        testIdPrefix="house-extras"
        cols={3}
      />

      {/* Tarifas — exibe apenas a tarifa principal (baseada na frequência do cliente).
          Outras frequências ficam num accordion opcional pra reduzir poluição visual. */}
      <RatesSection f={f} upd={upd} />

      <AccessInstructionsCollapsible f={f} upd={upd} />

      {/* Cobrança */}
      <div>
        <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
          💵 Cobrança
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Dia do mês para pagamento">
            <input
              type="number"
              min="1"
              max="31"
              data-testid="house-payment-due-day"
              className="cm-input"
              placeholder="ex: 5 (todo dia 5 do mês)"
              value={f.payment_due_day ?? ""}
              onChange={(e) => upd("payment_due_day", e.target.value)}
            />
          </Field>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          Definindo este campo, o cliente aparecerá automaticamente no relatório de <strong>Recebimentos</strong>.
        </p>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Tarifas — versão simplificada: exibe APENAS a tarifa correspondente à
// frequência selecionada. Outras tarifas ficam atrás de um botão "Adicionar
// tarifas para outras frequências" pra reduzir ruído visual.
// ─────────────────────────────────────────────────────────────────────────
function RatesSection({ f, upd }) {
  // Mapeia a frequência do cliente ao campo de tarifa correspondente
  const FREQ_TO_RATE = {
    "Semanal": { key: "rate_weekly", label: "Por Limpeza Semanal" },
    "Quinzenal": { key: "rate_biweekly", label: "Por Limpeza Quinzenal" },
    "Mensal": { key: "rate_monthly", label: "Por Limpeza Mensal" },
    "2x por mês": { key: "rate_biweekly", label: "Por Limpeza Quinzenal" },
  };
  const principal = FREQ_TO_RATE[f.frequency] || {
    key: "rate_other",
    label: f.frequency ? `Por ${f.frequency}` : "Tarifa por limpeza",
  };

  // Mostra outras tarifas se já há valor cadastrado em alguma delas
  const otherRates = [
    { key: "rate_weekly", label: "Semanal" },
    { key: "rate_biweekly", label: "Quinzenal" },
    { key: "rate_monthly", label: "Mensal" },
    { key: "rate_other", label: "Outra frequência" },
  ].filter((r) => r.key !== principal.key);

  const hasOtherValue = otherRates.some((r) => Number(f[r.key]) > 0);
  const [expanded, setExpanded] = React.useState(hasOtherValue);

  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
        Tarifa por limpeza (US$)
      </div>
      <FormField label={principal.label}>
        <DollarInput
          testId={`house-${principal.key}`}
          value={f[principal.key]}
          onChange={(v) => upd(principal.key, v)}
        />
      </FormField>

      {!expanded ? (
        <button
          type="button"
          data-testid="house-rates-expand"
          onClick={() => setExpanded(true)}
          className="mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
        >
          + Adicionar tarifas para outras frequências
        </button>
      ) : (
        <div className="mt-3 space-y-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Outras tarifas (opcional)
            </span>
            <button
              type="button"
              data-testid="house-rates-collapse"
              onClick={() => setExpanded(false)}
              className="text-[11px] text-slate-500 hover:text-slate-700"
            >
              Ocultar
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {otherRates.map((r) => (
              <FormField key={r.key} label={r.label}>
                <DollarInput
                  testId={`house-${r.key}`}
                  value={f[r.key]}
                  onChange={(v) => upd(r.key, v)}
                />
              </FormField>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Generic chip group colapsado: oculto por padrão, expande ao clicar.
// Expande automaticamente se já houver itens selecionados (edição de cliente).
// ─────────────────────────────────────────────────────────────────────────
function CollapsibleChipGroup({ label, emptyHint, options, value, onChange, testIdPrefix, cols = 3 }) {
  const selected = Array.isArray(value) ? value : [];
  const [expanded, setExpanded] = React.useState(selected.length > 0);

  if (!expanded) {
    return (
      <button
        type="button"
        data-testid={`${testIdPrefix}-expand`}
        onClick={() => setExpanded(true)}
        className="group w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 border-dashed border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-500 text-left transition-all shadow-sm hover:shadow-md"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
            <Plus size={18} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{label}{" "}
              <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">· opcional</span>
            </div>
            {emptyHint && <div className="text-xs text-slate-600 mt-0.5 truncate">{emptyHint}</div>}
          </div>
        </div>
        <span className="text-blue-700 font-semibold text-xs whitespace-nowrap group-hover:translate-x-1 transition-transform">
          Adicionar →
        </span>
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        {selected.length === 0 && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-collapse`}
            onClick={() => setExpanded(false)}
            className="text-[11px] text-slate-500 hover:text-slate-700"
          >
            Ocultar
          </button>
        )}
      </div>
      <ChipGroup
        label=""
        options={options}
        value={value}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
        cols={cols}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Instruções de Acesso — colapsada por padrão.
// Expande automaticamente se já houver instruções cadastradas.
// ─────────────────────────────────────────────────────────────────────────
function AccessInstructionsCollapsible({ f, upd }) {
  const hasValue = Boolean((f.access_instructions || "").trim());
  const [expanded, setExpanded] = React.useState(hasValue);

  if (!expanded) {
    return (
      <button
        type="button"
        data-testid="house-access-expand"
        onClick={() => setExpanded(true)}
        className="group w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 border-dashed border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-500 text-left transition-all shadow-sm hover:shadow-md"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 transition-transform">
            <Plus size={18} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              🔑 Instruções da Chave / Acesso{" "}
              <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">· opcional</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5 truncate">
              Código da porta, alarme, onde deixar a chave…
            </div>
          </div>
        </div>
        <span className="text-blue-700 font-semibold text-xs whitespace-nowrap group-hover:translate-x-1 transition-transform">
          Adicionar →
        </span>
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">
          🔑 Instruções da Chave / Acesso
        </span>
        {!hasValue && (
          <button
            type="button"
            data-testid="house-access-collapse"
            onClick={() => setExpanded(false)}
            className="text-[11px] text-slate-500 hover:text-slate-700"
          >
            Ocultar
          </button>
        )}
      </div>
      <textarea
        data-testid="house-access-instructions"
        rows={3}
        className="cm-input"
        placeholder="Código da porta, alarme, onde deixar a chave…"
        value={f.access_instructions || ""}
        onChange={(e) => upd("access_instructions", e.target.value)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Dias da semana — pílulas compactas em uma única linha (mais leve que ChipGroup).
// Pílula circular ~36px com fundo azul quando selecionada.
// ─────────────────────────────────────────────────────────────────────────


